import { supabase } from '@/lib/supabase';
import type { FilterState } from '@/types/lead-attribution';
import type { LeadMatch, PaginationState } from '../types';

export const fetchWebsiteLeads = async (
  filters: FilterState,
  pagination?: PaginationState
) => {
  try {
    console.log(
      'Fetching website leads with filters:',
      filters,
      'pagination:',
      pagination
    );

    // Base query for count
    let countQuery = supabase
      .from('leads')
      .select('*', { count: 'exact', head: true });

    // Base query for data
    let query = supabase
      .from('leads')
      .select(
        `
        id,
        client_name,
        client_email,
        client_phone,
        service_street,
        service_requested,
        service_slug,
        message,
        status,
        submission_url,
        created_at
      `
      )
      .order('created_at', { ascending: false });

    // Apply filters to both queries
    if (filters.dateRange) {
      const startDate = filters.dateRange.start.toISOString();
      const endDate = filters.dateRange.end.toISOString();
      countQuery = countQuery
        .gte('created_at', startDate)
        .lte('created_at', endDate);
      query = query.gte('created_at', startDate).lte('created_at', endDate);
    }

    // Apply time frame filter
    if (filters.timeFrame && filters.timeFrame !== 'all') {
      const now = new Date();
      let startDate: Date;

      switch (filters.timeFrame) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'thisyear':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(0);
      }

      const startDateISO = startDate.toISOString();
      countQuery = countQuery.gte('created_at', startDateISO);
      query = query.gte('created_at', startDateISO);
    }

    // Apply service requested filter (similar to source)
    if (filters.source) {
      countQuery = countQuery.ilike('service_requested', `%${filters.source}%`);
      query = query.ilike('service_requested', `%${filters.source}%`);
    }

    // Apply pagination
    if (pagination) {
      const from = (pagination.page - 1) * pagination.pageSize;
      const to = from + pagination.pageSize - 1;
      query = query.range(from, to);
    }

    // Get total count
    const { count, error: countError } = await countQuery;
    if (countError) throw countError;

    // Get data
    const { data, error } = await query;
    if (error) throw error;

    console.log(
      `Real website leads received: ${data?.length} records of ${count} total`
    );
    return { data: data || [], total: count || 0 };
  } catch (error) {
    console.error('Error fetching website leads:', error);
    throw error;
  }
};

// This week's leads by city (case-insensitive match on location or street)
export const fetchTodayLeadsByCity = async (city: string) => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  // Start from 7 days ago
  const start = new Date();
  start.setDate(start.getDate() - 7);
  start.setHours(0, 0, 0, 0);

  console.log(`[WeekLeads ${city}] Query: ${start.toISOString()} to ${end.toISOString()}`);

  // Build search patterns - include variations for North York
  let searchPatterns: string[];
  if (city === 'North York') {
    // North York can appear as "North York", "Northyork", "North-York", or even "Toronto" (since it's part of Toronto)
    searchPatterns = ['%North York%', '%Northyork%', '%North-York%'];
  } else {
    searchPatterns = [`%${city}%`];
  }

  // Build OR condition for all patterns
  const orConditions = searchPatterns.flatMap(p => [
    `location.ilike.${p}`,
    `service_street.ilike.${p}`
  ]).join(',');

  console.log(`[WeekLeads ${city}] Searching with patterns:`, searchPatterns);

  const { data, error } = await supabase
    .from('leads')
    .select('id,status,location,service_street,created_at')
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString())
    .or(orConditions);

  if (error) {
    console.error(`[WeekLeads ${city}] Error:`, error);
    throw error;
  }

  console.log(`[WeekLeads ${city}] Found ${data?.length || 0} leads`);

  const byStatus: Record<
    'new' | 'contacted' | 'qualified' | 'quoted' | 'won' | 'lost',
    number
  > = {
    new: 0,
    contacted: 0,
    qualified: 0,
    quoted: 0,
    won: 0,
    lost: 0,
  };
  for (const row of data || []) {
    const s = (row.status || 'new') as keyof typeof byStatus;
    if (byStatus[s] !== undefined) byStatus[s]++;
  }

  return {
    total: data?.length || 0,
    byStatus,
  };
};

export const fetchLeadMatches = async (
  filters: FilterState
): Promise<LeadMatch[]> => {
  try {
    console.log('Fetching lead matches with filters:', filters);

    // Query with proper JOIN to leads table for real client data
    let query = supabase
      .from('lead_request_matches')
      .select(`
        *,
        leads:lead_id (
          id,
          client_name,
          client_email,
          client_phone,
          service_requested,
          service_street,
          created_at
        )
      `)
      .order('created_at', { ascending: false });

    if (filters.franchise && filters.franchise !== 'all') {
      query = query.eq('franchise', filters.franchise);
    }

    if (filters.status && filters.status !== 'all') {
      if (filters.status === 'matched') {
        query = query.neq('match_type', 'none');
      } else if (filters.status === 'unmatched') {
        query = query.eq('match_type', 'none');
      }
    }

    if (filters.dateRange) {
      query = query
        .gte('created_at', filters.dateRange.start.toISOString())
        .lte('created_at', filters.dateRange.end.toISOString());
    }

    // Apply time frame filter
    if (filters.timeFrame && filters.timeFrame !== 'all') {
      const now = new Date();
      let startDate: Date;

      switch (filters.timeFrame) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'thisyear':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(0);
      }

      query = query.gte('created_at', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error('Database query error:', error);
      throw error;
    }

    console.log('Real lead matches received:', data?.length, 'records');

    // Transform the data - leads is now populated from the JOIN
    return (data || []).map((match) => {
      // Handle both joined data and fallback for unmatched records
      const leadData = match.leads;

      return {
        ...match,
        leads: leadData
          ? {
              id: leadData.id,
              client_name: leadData.client_name || 'Unknown',
              client_email: leadData.client_email || null,
              client_phone: leadData.client_phone || null,
              requested_service: leadData.service_requested || 'Unknown',
              street_address: leadData.service_street ||
                (match.match_details?.requestData?.address
                  ? `${match.match_details.requestData.address.street1}, ${match.match_details.requestData.address.city} ${match.match_details.requestData.address.province}`
                  : 'Unknown'),
              created_at: leadData.created_at || match.created_at,
            }
          : null,
      };
    });
  } catch (error) {
    console.error('Error fetching lead matches:', error);
    throw error;
  }
};
